/**
 * Device communication initialization and setup.
 * Handles WebSerial, WebBLE, and WebUSB-FTDI device managers.
 */

import { ConnectionState, DeviceCommunicationPanel, MessageType } from 'methodscript-device';
import type { SerialDeviceInfo, BleDeviceInfo, UsbFtdiDeviceInfo } from 'methodscript-device';
import { WebBLEManager } from 'methodscript-device/webble';
import { WebSerialManager } from 'methodscript-device/webserial';
import { WebUSBFTDIManager } from 'methodscript-device/webusb-ftdi';
import { MethodScriptPackageParser, DEFAULT_VARTYPES, applyPrefix, interpretMetadata } from 'methodscript-parser';
import * as monaco from 'monaco-editor';
import { logger } from 'shared-monaco-methodscript';

import { showConfirmDialog } from './confirm-dialog.js';
import { showToast, ToastType } from './toast-notification.js';
import type { StatusCallback } from './validation.js';

/**
 * Device managers container
 */
export type DeviceManagers = {
    webserialManager: WebSerialManager;
    webbleManager: WebBLEManager;
    webusbFtdiManager: WebUSBFTDIManager;
    activeDeviceManager: WebSerialManager | WebBLEManager | WebUSBFTDIManager | null;
    communicationPanel: DeviceCommunicationPanel;
    packageParser: MethodScriptPackageParser;
};

/**
 * Detect if running on Android
 */
export function isAndroid(): boolean {
    return /Android/i.test(navigator.userAgent);
}

/**
 * Navigate to a specific line and column in the editor
 * MethodSCRIPT format: line 0 is "e", line 1 is first script command
 * Error messages report line numbers starting from 1 (first script line after "e")
 * Monaco API uses 1-based line numbers: line 1 is "e", line 2 is first script command
 */
export function navigateToError(editor: monaco.editor.IStandaloneCodeEditor, line: number, column: number): void {
    if (!editor) return;

    // Error "Line 1" should navigate to Monaco line 2 (line + 1)
    const monacoLine = line + 1;
    const monacoColumn = column;

    // Reveal the position and focus the editor
    editor.revealPositionInCenter({ lineNumber: monacoLine, column: monacoColumn });
    editor.setPosition({ lineNumber: monacoLine, column: monacoColumn });
    editor.focus();
}

/**
 * SI prefix symbol mapping
 */
const SI_PREFIX_SYMBOLS: Record<string, string> = {
    'a': 'a',
    'f': 'f',
    'p': 'p',
    'n': 'n',
    'u': 'µ',
    'm': 'm',
    'k': 'k',
    'M': 'M',
    'G': 'G',
    'T': 'T',
    'P': 'P',
    'E': 'E',
    'i': '',
    ' ': '',
};

/**
 * Formatting constants for raw value display
 */
/** Threshold above which values use thousand separators */
const LARGE_VALUE_THRESHOLD = 1e6;
/** Maximum decimal places for large non-integer values */
const LARGE_VALUE_MAX_DECIMALS = 4;
/** Maximum decimal places for small fractional values (< 1) */
const SMALL_VALUE_MAX_DECIMALS = 6;
/** Maximum decimal places for normal-range values */
const NORMAL_VALUE_MAX_DECIMALS = 4;

/**
 * Format a raw value for display (not scientific notation)
 */
function formatRawValue(rawValue: number): string {
    const absValue = Math.abs(rawValue);
    if (absValue === 0) {
        return '0';
    } else if (Number.isInteger(rawValue) && absValue >= LARGE_VALUE_THRESHOLD) {
        // For large integers, use thousand separators
        return rawValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
    } else if (absValue >= LARGE_VALUE_THRESHOLD) {
        // For large non-integers, use thousand separators with limited decimals
        return rawValue.toLocaleString('en-US', { maximumFractionDigits: LARGE_VALUE_MAX_DECIMALS });
    } else if (absValue < 1 && absValue > 0) {
        return rawValue.toFixed(SMALL_VALUE_MAX_DECIMALS).replace(/\.?0+$/, '');
    } else {
        return rawValue.toFixed(NORMAL_VALUE_MAX_DECIMALS).replace(/\.?0+$/, '');
    }
}

/**
 * Parse incoming data for MethodScript packages and display them
 */
export function parseAndDisplayDataPackages(data: string, packageParser: MethodScriptPackageParser, communicationPanel: DeviceCommunicationPanel): void {
    // Only parse if the checkbox is enabled
    if (!packageParser || !communicationPanel.isParsePackagesEnabled()) return;

    try {
        // Extract all complete packages from the data
        const packages = packageParser.extractPackages(data);

        // Process each package
        for (const packageStr of packages) {
            const parsedPackage = packageParser.parsePackage(packageStr);

            // Create condensed single-line messages for each variable
            const dataLines: string[] = [];

            for (const variable of parsedPackage.variables) {
                const vartypeInfo = variable.varTypeInfo;
                const description = vartypeInfo ? vartypeInfo.description : 'Unknown variable';

                // Use raw value with SI prefix symbol to avoid scientific notation
                const rawValue = variable.value;
                let valueStr: string;

                try {
                    // Verify the prefix is valid
                    applyPrefix(rawValue, variable.prefix);

                    const symbol = SI_PREFIX_SYMBOLS[variable.prefix] ?? variable.prefix;

                    // Format raw value as normal double (not scientific notation)
                    const formattedValue = formatRawValue(rawValue);

                    // Determine unit based on vartype (simplified)
                    let unit = '';
                    if (vartypeInfo) {
                        const name = vartypeInfo.name.toLowerCase();
                        if (name.includes('potential') || name.includes('voltage')) {
                            unit = 'V';
                        } else if (name.includes('current')) {
                            unit = 'A';
                        } else if (name.includes('time')) {
                            unit = 's';
                        }
                    }

                    valueStr = `${formattedValue} ${symbol}${unit}`.trim();
                } catch (error) {
                    logger.error('[DeviceComm] Failed to format value:', error);
                    valueStr = `${rawValue} [unknown prefix: ${variable.prefix}]`;
                }

                // Build metadata string
                let metadataStr = '';
                if (variable.metadata && variable.metadata.length > 0) {
                    const metaParts = variable.metadata.map((meta) => {
                        const interpretation = interpretMetadata(meta);
                        return interpretation;
                    });
                    metadataStr = ` (${metaParts.join(', ')})`;
                }

                // Single-line format: • Description (varType): value + metadata
                const line = `• ${description} (${variable.varType}): ${valueStr}${metadataStr}`;
                dataLines.push(line);
            }

            const dataMessage = dataLines.join('\n');

            // Add the data message to the communication panel
            communicationPanel.addMessage(MessageType.Data, dataMessage);
        }
    } catch (error) {
        // Silently ignore parsing errors for non-package data
        console.debug('Package parsing error (non-critical):', error);
    }
}

/**
 * Update device status UI
 */
export function updateDeviceStatus(state: ConnectionState, deviceType: 'serial' | 'ble' | 'usb-ftdi'): void {
    const statusIndicator = document.querySelector('.status-indicator');
    const statusText = document.getElementById('deviceStatusText');
    const serialConnectBtn = document.getElementById('deviceConnectBtn') as HTMLButtonElement;
    const bleConnectBtn = document.getElementById('deviceConnectBLEBtn') as HTMLButtonElement;
    const usbFtdiBtn = document.getElementById('deviceConnectUSBBtn') as HTMLButtonElement;
    const sendBtn = document.getElementById('deviceSendBtn') as HTMLButtonElement;

    if (!statusIndicator || !statusText || !serialConnectBtn || !bleConnectBtn || !sendBtn) return;

    // Update status indicator
    statusIndicator.className = `status-indicator ${state}`;

    // Determine which button to update
    let connectBtn: HTMLButtonElement;
    let otherBtns: HTMLButtonElement[];
    if (deviceType === 'ble') {
        connectBtn = bleConnectBtn;
        otherBtns = [serialConnectBtn, usbFtdiBtn];
    } else if (deviceType === 'usb-ftdi') {
        connectBtn = usbFtdiBtn;
        otherBtns = [serialConnectBtn, bleConnectBtn];
    } else {
        connectBtn = serialConnectBtn;
        otherBtns = [bleConnectBtn, usbFtdiBtn];
    }

    // Update status text and buttons
    switch (state) {
        case ConnectionState.Disconnected:
            statusText.textContent = 'Disconnected';
            serialConnectBtn.textContent = 'Connect with USB';
            serialConnectBtn.classList.remove('connected');
            bleConnectBtn.textContent = 'Connect with Bluetooth';
            bleConnectBtn.classList.remove('connected');
            usbFtdiBtn.textContent = 'Connect (USB-FTDI)';
            usbFtdiBtn.classList.remove('connected');
            sendBtn.disabled = true;
            break;

        case ConnectionState.Connecting:
            statusText.textContent = 'Connecting...';
            connectBtn.textContent = 'Connecting...';
            connectBtn.disabled = true;
            otherBtns.forEach((btn) => {
                if (btn) btn.disabled = true;
            });
            sendBtn.disabled = true;
            break;

        case ConnectionState.Connected:
            const deviceTypeName = deviceType === 'ble' ? 'BLE' : deviceType === 'usb-ftdi' ? 'USB-FTDI' : 'Serial';
            statusText.textContent = `Connected (${deviceTypeName})`;
            connectBtn.textContent = 'Disconnect';
            connectBtn.classList.add('connected');
            connectBtn.disabled = false;
            otherBtns.forEach((btn) => {
                if (btn) btn.disabled = true;
            }); // Disable other connection methods
            sendBtn.disabled = false;
            break;

        case ConnectionState.Error:
            statusText.textContent = 'Connection Error';
            serialConnectBtn.textContent = 'Connect with USB';
            serialConnectBtn.classList.remove('connected');
            serialConnectBtn.disabled = false;
            bleConnectBtn.textContent = 'Connect with Bluetooth';
            bleConnectBtn.classList.remove('connected');
            bleConnectBtn.disabled = false;
            usbFtdiBtn.textContent = 'Connect (USB-FTDI)';
            usbFtdiBtn.classList.remove('connected');
            usbFtdiBtn.disabled = false;
            sendBtn.disabled = true;
            break;
    }
}

/**
 * Set up Web Serial event listeners
 */
function setupWebSerialEventListeners(managers: DeviceManagers, updateStatus: StatusCallback): void {
    const { webserialManager, communicationPanel, packageParser } = managers;

    // State change events
    webserialManager.addEventListener('stateChange', ((event: CustomEvent<{ state: ConnectionState }>) => {
        const state = event.detail.state;
        updateDeviceStatus(state, 'serial');
    }) as EventListener);

    // Connected event
    webserialManager.addEventListener('connected', ((event: CustomEvent<SerialDeviceInfo>) => {
        managers.activeDeviceManager = webserialManager;
        const deviceInfo = event.detail;
        const deviceId = deviceInfo.usbVendorId ? `VID:0x${deviceInfo.usbVendorId.toString(16)}` : 'Serial Device';
        communicationPanel.addMessage(MessageType.Info, `Connected to ${deviceId} (Serial)`);
        updateStatus(`Connected to ${deviceId} (Serial)`, false);
    }) as EventListener);

    // Disconnected event
    webserialManager.addEventListener('disconnected', () => {
        if (managers.activeDeviceManager === webserialManager) {
            managers.activeDeviceManager = null;
        }
        communicationPanel.addMessage(MessageType.Info, 'Disconnected from serial device');
        updateStatus('Disconnected from device', false);
    });

    // Data sent event
    webserialManager.addEventListener('dataSent', ((event: CustomEvent<{ data: string; bytes: number }>) => {
        const { data, bytes } = event.detail;
        communicationPanel.addMessage(MessageType.Sent, data);
        logger.debug(`Sent ${bytes} bytes to device (Serial)`);
    }) as EventListener);

    // Data received event
    webserialManager.addEventListener('dataReceived', ((event: CustomEvent<{ data: string }>) => {
        const { data } = event.detail;
        communicationPanel.addMessage(MessageType.Received, data);

        // Parse data packages if any
        parseAndDisplayDataPackages(data, packageParser, communicationPanel);
    }) as EventListener);

    // Error event
    webserialManager.addEventListener('error', ((event: CustomEvent<{ message: string }>) => {
        const { message } = event.detail;
        communicationPanel.addMessage(MessageType.Error, message);
        updateStatus(`Error: ${message}`, true);
    }) as EventListener);
}

/**
 * Set up WebUSB-FTDI event listeners
 */
function setupWebUSBFTDIEventListeners(managers: DeviceManagers, updateStatus: StatusCallback): void {
    const { webusbFtdiManager, communicationPanel, packageParser } = managers;

    // State change events
    webusbFtdiManager.addEventListener('stateChange', ((event: CustomEvent<{ state: ConnectionState }>) => {
        const state = event.detail.state;
        updateDeviceStatus(state, 'usb-ftdi');
    }) as EventListener);

    // Connected event
    webusbFtdiManager.addEventListener('connected', ((event: CustomEvent<UsbFtdiDeviceInfo>) => {
        managers.activeDeviceManager = webusbFtdiManager;
        const deviceInfo = event.detail;
        const deviceId = deviceInfo.productName || `USB:0x${deviceInfo.vendorId.toString(16)}:0x${deviceInfo.productId.toString(16)}`;
        communicationPanel.addMessage(MessageType.Info, `Connected to ${deviceId} (WebUSB-FTDI)`);
        updateStatus(`Connected to ${deviceId} (USB-FTDI)`, false);
    }) as EventListener);

    // Disconnected event
    webusbFtdiManager.addEventListener('disconnected', () => {
        if (managers.activeDeviceManager === webusbFtdiManager) {
            managers.activeDeviceManager = null;
        }
        communicationPanel.addMessage(MessageType.Info, 'Disconnected from WebUSB-FTDI device');
        updateStatus('Disconnected from device', false);
    });

    // Data sent event
    webusbFtdiManager.addEventListener('dataSent', ((event: CustomEvent<{ data: string; bytes: number }>) => {
        const { data, bytes } = event.detail;
        communicationPanel.addMessage(MessageType.Sent, data);
        logger.debug(`Sent ${bytes} bytes to device (WebUSB-FTDI)`);
    }) as EventListener);

    // Data received event
    webusbFtdiManager.addEventListener('dataReceived', ((event: CustomEvent<{ data: string }>) => {
        const { data } = event.detail;
        communicationPanel.addMessage(MessageType.Received, data);

        // Parse data packages if any
        parseAndDisplayDataPackages(data, packageParser, communicationPanel);
    }) as EventListener);

    // Error event
    webusbFtdiManager.addEventListener('error', ((event: CustomEvent<{ message: string }>) => {
        const { message } = event.detail;
        communicationPanel.addMessage(MessageType.Error, message);
        updateStatus(`Error: ${message}`, true);
    }) as EventListener);
}

/**
 * Set up Web Bluetooth LE event listeners
 */
function setupWebBLEEventListeners(managers: DeviceManagers, updateStatus: StatusCallback): void {
    const { webbleManager, communicationPanel, packageParser } = managers;

    // State change events
    webbleManager.addEventListener('stateChange', ((event: CustomEvent<{ state: ConnectionState }>) => {
        const state = event.detail.state;
        updateDeviceStatus(state, 'ble');
    }) as EventListener);

    // Connected event
    webbleManager.addEventListener('connected', ((event: CustomEvent<BleDeviceInfo>) => {
        managers.activeDeviceManager = webbleManager;
        const deviceInfo = event.detail;
        const deviceName = deviceInfo.name || 'BLE Device';
        communicationPanel.addMessage(MessageType.Info, `Connected to ${deviceName} (Bluetooth LE)`);
        updateStatus(`Connected to ${deviceName} (BLE)`, false);
    }) as EventListener);

    // Disconnected event
    webbleManager.addEventListener('disconnected', () => {
        if (managers.activeDeviceManager === webbleManager) {
            managers.activeDeviceManager = null;
        }
        communicationPanel.addMessage(MessageType.Info, 'Disconnected from BLE device');
        updateStatus('Disconnected from device', false);
    });

    // Data sent event
    webbleManager.addEventListener('dataSent', ((event: CustomEvent<{ data: string; bytes: number }>) => {
        const { data, bytes } = event.detail;
        communicationPanel.addMessage(MessageType.Sent, data);
        logger.debug(`Sent ${bytes} bytes to device (BLE)`);
    }) as EventListener);

    // Data received event
    webbleManager.addEventListener('dataReceived', ((event: CustomEvent<{ data: string }>) => {
        const { data } = event.detail;
        communicationPanel.addMessage(MessageType.Received, data);

        // Parse data packages if any
        parseAndDisplayDataPackages(data, packageParser, communicationPanel);
    }) as EventListener);

    // Error event
    webbleManager.addEventListener('error', ((event: CustomEvent<{ message: string }>) => {
        const { message } = event.detail;
        communicationPanel.addMessage(MessageType.Error, message);
        updateStatus(`Error: ${message}`, true);
    }) as EventListener);
}

/**
 * Set up device button handlers
 */
function setupDeviceButtonHandlers(managers: DeviceManagers, sendScriptCallback: () => Promise<void>): void {
    const { webserialManager, webbleManager, webusbFtdiManager, communicationPanel } = managers;

    // Serial connect button
    document.getElementById('deviceConnectBtn')?.addEventListener('click', async () => {
        try {
            // Disconnect BLE if connected
            if (webbleManager.getState() === ConnectionState.Connected) {
                await webbleManager.disconnect();
            }

            if (webserialManager.getState() === ConnectionState.Connected) {
                // Disconnect Serial
                await webserialManager.disconnect();
            } else {
                // Connect Serial
                await webserialManager.connect();
            }
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            showToast(ToastType.Warning, errorMsg);
            communicationPanel.addMessage(MessageType.Error, `Serial connection failed: ${errorMsg}`);
            communicationPanel.show();
        }
    });

    // BLE connect button
    document.getElementById('deviceConnectBLEBtn')?.addEventListener('click', async () => {
        try {
            // Disconnect Serial if connected
            if (webserialManager.getState() === ConnectionState.Connected) {
                await webserialManager.disconnect();
            }

            if (webbleManager.getState() === ConnectionState.Connected) {
                // Disconnect BLE
                await webbleManager.disconnect();
            } else {
                // Connect BLE
                await webbleManager.connect();
            }
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            showToast(ToastType.Warning, errorMsg);
            communicationPanel.addMessage(MessageType.Error, `BLE connection failed: ${errorMsg}`);
            communicationPanel.show();
        }
    });

    // USB-FTDI connect button (Android only)
    document.getElementById('deviceConnectUSBBtn')?.addEventListener('click', async () => {
        try {
            // Disconnect other managers if connected
            if (webserialManager.getState() === ConnectionState.Connected) {
                await webserialManager.disconnect();
            }
            if (webbleManager.getState() === ConnectionState.Connected) {
                await webbleManager.disconnect();
            }

            if (webusbFtdiManager.getState() === ConnectionState.Connected) {
                // Disconnect USB-FTDI
                await webusbFtdiManager.disconnect();
            } else {
                // Connect USB-FTDI - baud rate will be auto-detected based on device
                await webusbFtdiManager.connect();
            }
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            showToast(ToastType.Warning, errorMsg);
            communicationPanel.addMessage(MessageType.Error, `USB-FTDI connection failed: ${errorMsg}`);
            communicationPanel.show();
        }
    });

    // Send button
    document.getElementById('deviceSendBtn')?.addEventListener('click', () => {
        sendScriptCallback();
    });

    // Show communication button
    document.getElementById('showCommBtn')?.addEventListener('click', () => {
        communicationPanel.toggle();
    });
}

/**
 * Initialize device communication
 * @returns DeviceManagers object or null if no APIs are supported
 */
export function initializeDeviceCommunication(
    editor: monaco.editor.IStandaloneCodeEditor,
    monacoInstance: typeof monaco,
    updateStatus: StatusCallback,
): DeviceManagers | null {
    // Initialize device managers
    const webserialManager = new WebSerialManager();
    const webbleManager = new WebBLEManager();
    const webusbFtdiManager = new WebUSBFTDIManager();

    // Initialize package parser
    const packageParser = new MethodScriptPackageParser(DEFAULT_VARTYPES);

    // Navigation callback for error links
    const navigationCallback = (line: number, column: number) => {
        navigateToError(editor, line, column);
    };

    // Always initialize communication panel with navigation callback
    const notificationCallback = (level: 'info' | 'warn' | 'error', message: string) => {
        const toastType = level === 'error' ? ToastType.Error : level === 'warn' ? ToastType.Warning : ToastType.Info;
        showToast(toastType, message);
    };
    const communicationPanel = new DeviceCommunicationPanel('communicationPanel', navigationCallback, notificationCallback);

    const managers: DeviceManagers = {
        webserialManager,
        webbleManager,
        webusbFtdiManager,
        activeDeviceManager: null,
        communicationPanel,
        packageParser,
    };

    // Check for API support
    const serialSupported = webserialManager.isWebSerialSupported();
    const bleSupported = webbleManager.isWebBluetoothSupported();
    const usbFtdiSupported = webusbFtdiManager.isWebUSBFTDISupported();

    if (!serialSupported && !bleSupported && !usbFtdiSupported) {
        // Show warning if no APIs are supported
        const deviceStatusText = document.getElementById('deviceStatusText');
        if (deviceStatusText) {
            deviceStatusText.textContent = 'Browser not supported';
            deviceStatusText.title = 'Please use Chrome, Edge, or Opera to connect to devices';
        }

        // Mark device buttons as unsupported (visually disabled via CSS, but still clickable)
        const deviceConnectBtn = document.getElementById('deviceConnectBtn') as HTMLButtonElement;
        const deviceConnectBLEBtn = document.getElementById('deviceConnectBLEBtn') as HTMLButtonElement;
        const deviceSendBtn = document.getElementById('deviceSendBtn') as HTMLButtonElement;
        if (deviceSendBtn) deviceSendBtn.disabled = true;

        // Add click handlers that show an informative message
        const unsupportedMessage = 'Your browser does not support connecting to devices. Please use Chrome, Edge, or Opera.';
        deviceConnectBtn?.addEventListener('click', () => {
            showToast(ToastType.Warning, unsupportedMessage);
        });
        deviceConnectBLEBtn?.addEventListener('click', () => {
            showToast(ToastType.Warning, unsupportedMessage);
        });

        // Show a toast notification so the user immediately knows
        showToast(ToastType.Warning, unsupportedMessage);

        // Set up show communication button (still works without device support)
        document.getElementById('showCommBtn')?.addEventListener('click', () => {
            communicationPanel.toggle();
        });

        updateStatus('Device communication not supported. Use Chrome, Edge, or Opera.', true);
        logger.warn('No device communication APIs supported in this browser');
        return managers; // Return managers anyway for communication panel
    }

    // Show WebUSB-FTDI button only on Android
    const usbFtdiBtn = document.getElementById('deviceConnectUSBBtn') as HTMLButtonElement;
    if (usbFtdiBtn) {
        if (isAndroid() && usbFtdiSupported) {
            logger.debug('[Device] Android detected, showing WebUSB-FTDI button');
            usbFtdiBtn.style.display = 'inline-block';
        } else {
            usbFtdiBtn.style.display = 'none';
        }
    }

    // Disable buttons for unsupported APIs and add informative titles
    const serialConnectBtn = document.getElementById('deviceConnectBtn') as HTMLButtonElement;
    const bleConnectBtn = document.getElementById('deviceConnectBLEBtn') as HTMLButtonElement;

    if (serialConnectBtn && !serialSupported) {
        serialConnectBtn.title = 'USB connections are not supported in this browser. Use Chrome, Edge, or Opera.';
    }
    if (bleConnectBtn && !bleSupported) {
        bleConnectBtn.title = 'Bluetooth connections are not supported in this browser. Use Chrome, Edge, or Opera.';
    }
    if (usbFtdiBtn && !usbFtdiSupported && usbFtdiBtn.style.display !== 'none') {
        usbFtdiBtn.title = 'USB-FTDI connections are not supported in this browser. Use Chrome or Edge.';
    }

    // Set up event listeners for all managers
    setupWebSerialEventListeners(managers, updateStatus);
    setupWebBLEEventListeners(managers, updateStatus);
    setupWebUSBFTDIEventListeners(managers, updateStatus);

    // Create send script callback
    const sendScriptCallback = async () => {
        await sendScriptToDevice(editor, managers, monacoInstance, updateStatus);
    };

    // Set up device button handlers
    setupDeviceButtonHandlers(managers, sendScriptCallback);

    // Add keyboard shortcut for Send to Device (Ctrl+Shift+U / Cmd+Shift+U)
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyU, () => {
        sendScriptCallback();
    });

    return managers;
}

/**
 * Send script to device
 */
export async function sendScriptToDevice(
    editor: monaco.editor.IStandaloneCodeEditor,
    managers: DeviceManagers,
    monacoInstance: typeof monaco,
    updateStatus: StatusCallback,
): Promise<void> {
    const { activeDeviceManager, webserialManager, webbleManager, webusbFtdiManager, communicationPanel } = managers;

    if (!activeDeviceManager || !editor) {
        return;
    }

    // Check if connected
    let isConnected = false;
    if (activeDeviceManager === webserialManager) {
        isConnected = webserialManager.getState() === ConnectionState.Connected;
    } else if (activeDeviceManager === webbleManager) {
        isConnected = webbleManager.getState() === ConnectionState.Connected;
    } else if (activeDeviceManager === webusbFtdiManager) {
        isConnected = webusbFtdiManager.getState() === ConnectionState.Connected;
    }

    if (!isConnected) {
        showToast(ToastType.Warning, 'Not connected to a device. Please connect first.');
        return;
    }

    // Get script content
    const script = editor.getValue();

    if (script.trim().length === 0) {
        showToast(ToastType.Warning, 'Script is empty');
        return;
    }

    // Check for errors
    const model = editor.getModel();
    if (!model) return;

    const markers = monacoInstance.editor.getModelMarkers({ resource: model.uri });
    const errors = markers.filter((m) => m.severity === monacoInstance.MarkerSeverity.Error);

    if (errors.length > 0) {
        const send = await showConfirmDialog(`Script has ${errors.length} error(s). Send anyway?`, 'Send anyway', 'Cancel');
        if (!send) {
            return;
        }
    }

    try {
        // Show communication panel (toggle will check visibility internally)
        communicationPanel.show();

        // Wrap script with protocol framing: 'e\n' prefix and extra '\n' suffix
        // Device expects: e\n<content>\n\n
        const wrappedScript = `e\n${script}\n`;

        // Send script using active device manager
        await activeDeviceManager.sendScript(wrappedScript);
        updateStatus('Script sent successfully', false);
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        communicationPanel.addMessage(MessageType.Error, `Failed to send script: ${errorMsg}`);
        communicationPanel.show();
        updateStatus(`Send failed: ${errorMsg}`, true);
    }
}
